"use client";

import { TabBar } from "@/components/tab-bar";
import { useInfront } from "@/context/infront-context";
import {
  COUNTRIES,
  createInstrumentSlug,
  DEFAULT_CONTENT,
} from "@/services/infront/utils";
import { useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";

const ID = "extended-stock-list";
const STORAGE_KEY = "efn-stock-list-country";

type Country = (typeof COUNTRIES)[0];
type Chain = { feed: number; name: string; description: string };

function defaultChain(country: Country): Chain | null {
  const tabs = country.tabChains ?? country.stockChains.slice(0, 1);
  return tabs.length > 1 ? null : tabs[0];
}

const ExtendedStockList = () => {
  const router = useRouter();
  const { isReady, load } = useInfront();
  const widgetRef = useRef<any>(null);

  const [activeCountry, setActiveCountry] = useState<Country>(COUNTRIES[0]);
  const [activeChain, setActiveChain] = useState<Chain | null>(defaultChain(COUNTRIES[0]));

  useEffect(() => {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved) {
      const country = COUNTRIES.find((c) => c.displayName === saved);
      if (country) {
        setActiveCountry(country);
        setActiveChain(defaultChain(country));
      }
    }
  }, []);

  const tabs = activeCountry.tabChains ?? activeCountry.stockChains.slice(0, 1);
  const activeChains = activeChain ? [activeChain] : tabs;

  function handleCountryChange(displayName: string) {
    const country = COUNTRIES.find((c) => c.displayName === displayName) ?? COUNTRIES[0];
    sessionStorage.setItem(STORAGE_KEY, country.displayName);
    setActiveCountry(country);
    setActiveChain(defaultChain(country));
  }

  useEffect(() => {
    let active = true;

    load?.(["core", "highstock", "hollowcandlestick", "heikinashi"]).then(() => {
      if (!isReady || !active) return;

      widgetRef.current?.destroy();
      widgetRef.current = null;

      const widgetOptions = new window.Infront.QuoteListWidgetOptions();

      widgetOptions.onInstrumentSelected = (instrument: {
        feed: number;
        ticker: string;
      }) => {
        router.push(createInstrumentSlug(instrument.feed, instrument.ticker));
      };

      widgetOptions.useChains = true;
      widgetOptions.chains = activeChains;

      widgetOptions.defaultContent = DEFAULT_CONTENT;
      widgetOptions.enableChangeStatusColors = true;
      widgetOptions.instrumentTypes = ["STOCK"];

      widgetOptions.columns = [
        { name: "FULL_NAME", flag: true },
        "LAST",
        "PCT_CHANGE",
        "CHANGE",
        "S_MARKET_PRICE",
        "ACC_VOLUME",
        "TIME",
      ];

      widgetRef.current = window.infront.quoteList(`#${ID}`, widgetOptions);
    });

    return () => {
      active = false;
      widgetRef.current?.destroy();
      widgetRef.current = null;
    };
  }, [isReady, router, activeChain, activeCountry]);

  const tabBarTabs = [
    ...(tabs.length > 1 ? [{ label: "Alla", value: "__all__" }] : []),
    ...tabs.map((t) => ({ label: t.description, value: t.name })),
  ];
  const tabBarActive = activeChain === null ? "__all__" : (activeChain?.name ?? "__all__");

  return (
    <div className="card overflow-x-hidden">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-end mb-6 gap-2 min-w-0">
        <select
          value={activeCountry.displayName}
          onChange={(e) => handleCountryChange(e.target.value)}
          className="w-full lg:w-auto text-sm border border-sand-700 rounded px-2 py-1 bg-transparent lg:order-last lg:ml-4"
        >
          {COUNTRIES.map((c) => (
            <option key={c.displayName} value={c.displayName}>
              {c.displayName}
            </option>
          ))}
        </select>
        <TabBar
          tabs={tabBarTabs}
          active={tabBarActive}
          onChange={(v) => {
            if (v === "__all__") setActiveChain(null);
            else setActiveChain(tabs.find((t) => t.name === v) ?? null);
          }}
        />
      </div>
      <div id={ID} />
    </div>
  );
};

export default ExtendedStockList;
